import { useEffect, useState } from "react";
import { api, errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { Checkbox } from "../ui/checkbox";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { Clock, BarChart3 } from "lucide-react";

const DEFAULT_QUIET = { enabled: false, start: 21, end: 8, tz: "America/Toronto" };

export function CampaignDrillDialog({ campaign, onClose, onFollowUp }) {
  const [drillRows, setDrillRows] = useState(null);
  const drillCampaign = campaign;
  useEffect(() => {
    if (!campaign) return;
    setDrillRows(null);
    api.get(`/admin/message-campaigns/${campaign.id}/recipients`).then((r) => setDrillRows(r.data.recipients)).catch((e) => toast.error(errText(e)));
  }, [campaign]);
  return (
      <Dialog open={!!drillCampaign} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto bg-white" data-testid="campaign-drill-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><BarChart3 size={18} className="text-[#F5551E]" /> Who opened & replied</DialogTitle>
            <DialogDescription className="text-slate-500 truncate">{drillCampaign?.target_label} · {drillCampaign?.message}</DialogDescription>
          </DialogHeader>
          {!drillRows ? <Skeleton className="h-40 rounded-xl" /> : (
            <div className="space-y-1.5">
              {drillRows.map((r) => (
                <div key={r.user_id} className="flex items-center gap-2 text-sm border-b border-slate-100 py-1.5" data-testid={`drill-row-${r.user_id}`}>
                  <span className="flex-1 min-w-0">
                    <span className="font-medium text-[#12234A]">{r.name}</span>
                    <span className="text-xs text-slate-400 ml-1 truncate">{r.email}</span>
                  </span>
                  <span className={`text-xs font-semibold ${r.opened ? "text-sky-600" : "text-slate-300"}`}>{r.opened ? "👁 Opened" : "Unopened"}</span>
                  <span className={`text-xs font-semibold ${r.replied ? "text-emerald-600" : "text-slate-300"}`}>{r.replied ? "↩ Replied" : "No reply"}</span>
                  <Button onClick={() => onFollowUp(r)} variant="ghost" size="sm" data-testid={`drill-message-${r.user_id}`} className="h-7 text-[#F5551E] shrink-0">Follow up</Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
  );
}

export function QuietHoursDialog({ open, initial, onClose, onSaved }) {
  const [quietDraft, setQuietDraft] = useState(DEFAULT_QUIET);
  useEffect(() => { if (open) setQuietDraft(initial || DEFAULT_QUIET); }, [open, initial]);
  const saveQuiet = async () => {
    try { const r = await api.put("/admin/quiet-hours", quietDraft); onSaved(r.data); onClose(); toast.success("Quiet hours updated"); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="sm:max-w-md bg-white" data-testid="quiet-hours-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-[#12234A] flex items-center gap-2"><Clock size={18} className="text-indigo-500" /> Quiet hours</DialogTitle>
            <DialogDescription className="text-slate-500">Any message sent during these hours is automatically held and delivered when quiet hours end — so users aren't pinged in the middle of the night.</DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="quiet-enabled-label">
            <Checkbox checked={quietDraft.enabled} onCheckedChange={(v) => setQuietDraft((d) => ({ ...d, enabled: !!v }))} data-testid="quiet-enabled-toggle" />
            <span className="text-sm text-slate-700">Enable quiet hours</span>
          </label>
          <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="quiet-peruser-label">
            <Checkbox checked={quietDraft.per_user_tz} onCheckedChange={(v) => setQuietDraft((d) => ({ ...d, per_user_tz: !!v }))} data-testid="quiet-peruser-toggle" />
            <span className="text-sm text-slate-700">Use each recipient's own timezone <span className="text-xs text-slate-400">(from their province)</span></span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-500">From</Label>
              <Select value={String(quietDraft.start)} onValueChange={(v) => setQuietDraft((d) => ({ ...d, start: Number(v) }))}>
                <SelectTrigger data-testid="quiet-start" className="mt-1 h-9 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">{[...Array(24)].map((_, i) => <SelectItem key={i} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Until</Label>
              <Select value={String(quietDraft.end)} onValueChange={(v) => setQuietDraft((d) => ({ ...d, end: Number(v) }))}>
                <SelectTrigger data-testid="quiet-end" className="mt-1 h-9 rounded-xl text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white max-h-64">{[...Array(24)].map((_, i) => <SelectItem key={i} value={String(i)}>{String(i).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">{quietDraft.per_user_tz ? "Fallback timezone" : "Timezone"}</Label>
            <Select value={quietDraft.tz} onValueChange={(v) => setQuietDraft((d) => ({ ...d, tz: v }))}>
              <SelectTrigger data-testid="quiet-tz" className="mt-1 h-9 rounded-xl text-sm"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {["America/Toronto", "America/Vancouver", "America/Edmonton", "America/Winnipeg", "America/Halifax", "America/St_Johns"].map((z) => <SelectItem key={z} value={z}>{z.replace("America/", "")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} className="rounded-xl">Cancel</Button>
            <Button onClick={saveQuiet} data-testid="quiet-save" className="rounded-xl bg-[#12234A] hover:bg-[#1b3366] text-white">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
  );
}
